import type { RootState } from "@/app/store"

import { apiSlice } from "@/shared/api/api-slice"

import { toDuplicateWordsCustomError } from "../model/duplicate-error"
import { TRIGGER_MODE_CONFIGS } from "../model/mode-config"
import { initializeTriggerRule } from "../model/prohibited-word-slice"
import type { SecurityRule, TriggerRule, TriggerWordsSecurityRule } from "../model/types"

import type {
  DeleteProhibitedWordSecurityRuleRequest,
  DeleteProhibitedWordSecurityRuleResponse,
  DisableSecurityRuleRequest,
  DisableSecurityRuleResponse,
  DisableSecurityRuleTransformedResponse,
  EnableSecurityRuleRequest,
  EnableSecurityRuleResponse,
  EnableSecurityRuleTransformedResponse,
  GetSecurityRulesResponse,
  PatchTriggerWordRuleRequest,
  PatchTriggerWordRuleResponse,
  PatchTriggerWordRulesResponse,
  UpdateProhibitedWordSecurityRuleRequest,
  UpdateProhibitedWordSecurityRuleResponse,
  UpdateSecurityRuleResponse,
  UpdateSecurityRuleTransformedResponse,
  UpsertSecurityRuleRequest
} from "./types"

const buildPatchSecurityRuleBody = (request: UpsertSecurityRuleRequest) => ({
  is_enabled: request.is_enabled,
  warning: request.warning,
  punishment: request.punishment,
  kind: request.kind,
  config: request.config,
  expected_chat_context_hash: request.expected_chat_context_hash
})

const buildTriggerRulePayload = (prohibitedWordsRule: RootState["prohibitedWords"]): TriggerRule => {
  const modeConfig = TRIGGER_MODE_CONFIGS[prohibitedWordsRule.mode]

  return {
    index: prohibitedWordsRule.index,
    name: modeConfig.fixedRuleName ?? prohibitedWordsRule.name,
    filter: prohibitedWordsRule.filter,
    actions: prohibitedWordsRule.actions.filter(action => modeConfig.allowedActionTypes.includes(action.action_type))
  }
}

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false
  const status = "status" in error ? error.status : undefined
  return status === 404
}

const isTriggerWordsRule = (rule: SecurityRule): rule is TriggerWordsSecurityRule => rule.kind === "trigger_words"

const getTriggerWordsRuleFromResponse = (response: GetSecurityRulesResponse): TriggerWordsSecurityRule | null => {
  return response.result.rules.find(isTriggerWordsRule) ?? null
}

const withDefaultBlacklistName = (rule: TriggerRule, index: number): TriggerRule => ({
  ...rule,
  index,
  name: rule.name?.trim() || `Blacklist ${index + 1}`
})

interface UpsertBlacklistRuleRequest {
  chatId: number
  rule: TriggerRule
  /** Undo of a delete: the rule goes back exactly as stored, its index and name included. */
  restore?: boolean
}

export const updateSecurityRuleApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ["SecurityRules", "SecurityRule", "SafeList", "FeaturesStatus"]
  })
  .injectEndpoints({
    endpoints: builder => ({
      updateSecurityRule: builder.mutation<UpdateSecurityRuleTransformedResponse, UpsertSecurityRuleRequest>({
        query: request =>
          request.id
            ? {
                url: `/api/v1/security/rules/chat/${request.chat_id}/${request.id}`,
                method: "PATCH",
                body: buildPatchSecurityRuleBody(request)
              }
            : {
                url: `/api/v1/security/rules`,
                method: "PUT",
                body: request
              },
        transformResponse: (response: UpdateSecurityRuleResponse) => {
          return response.result.rules
        },
        transformErrorResponse: response => toDuplicateWordsCustomError(response) ?? response,
        invalidatesTags: (_result, error, { chat_id, id, kind }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chat_id },
                { type: "SecurityRule" as const, id: `${chat_id}-${id ?? kind}` },
                { type: "SecurityRule" as const, id: `${chat_id}-${kind}` },
                ...(kind === "trigger_words" ? [{ type: "SafeList" as const, id: chat_id }] : []),
                { type: "FeaturesStatus" as const, id: chat_id }
              ]
      }),

      updateProhibitedWordSecurityRule: builder.mutation<
        UpdateProhibitedWordSecurityRuleResponse,
        UpdateProhibitedWordSecurityRuleRequest
      >({
        async queryFn({ chatId }, { getState, dispatch }, _extra, baseQuery) {
          const state = getState() as RootState
          const prohibitedWordsRule = state.prohibitedWords
          const nextRule = buildTriggerRulePayload(prohibitedWordsRule)

          const existingResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}`,
            method: "GET"
          })

          if (existingResult.error && !isNotFoundError(existingResult.error)) {
            return { error: toDuplicateWordsCustomError(existingResult.error) ?? existingResult.error }
          }

          const existingRule = existingResult.error
            ? null
            : getTriggerWordsRuleFromResponse(existingResult.data as GetSecurityRulesResponse)
          if (prohibitedWordsRule.mode === "support_mode" && existingRule) {
            const supportRule = existingRule.config.rules.find(rule =>
              rule.actions.some(action => action.action_type === "support_mode")
            )
            nextRule.index =
              supportRule?.index ?? Math.max(-1, ...existingRule.config.rules.map(rule => rule.index)) + 1
          }

          const patchByKind = !existingRule || existingRule.id === null
          const patchResult = await baseQuery({
            url: patchByKind
              ? `/api/v1/security/rules/chat/${chatId}/trigger-rules`
              : `/api/v1/security/rules/chat/${chatId}/${existingRule.id}/trigger-rules`,
            method: "PATCH",
            body: patchByKind ? { rule: nextRule } : { upsert: [nextRule] }
          })

          if (patchResult.error) {
            return { error: toDuplicateWordsCustomError(patchResult.error) ?? patchResult.error }
          }

          const patchResponse = patchResult.data as PatchTriggerWordRulesResponse
          if (!patchResponse.result.rules) {
            return { error: { status: 500, data: "Trigger rules patch returned no rule" } }
          }
          const updatedRule = patchResponse.result.rules
          const savedIndex =
            updatedRule.kind === "trigger_words" && prohibitedWordsRule.mode === "support_mode"
              ? (updatedRule.config.rules.find(rule =>
                  rule.actions.some(action => action.action_type === "support_mode")
                )?.index ?? nextRule.index)
              : nextRule.index

          dispatch(
            initializeTriggerRule({
              mode: prohibitedWordsRule.mode,
              seedRule: { ...prohibitedWordsRule, index: savedIndex }
            })
          )

          return { data: updatedRule }
        },
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-trigger_words` },
                { type: "SafeList" as const, id: chatId },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      }),

      /**
       * Append-only "create blacklist" mutation used by the routed
       * `/moderation/blacklist/*` create page. Smart-falls-back to PUT-create
       * when the umbrella `trigger_words` rule doesn't exist yet; otherwise
       * PATCHes the umbrella with the new rule wrapped in `upsert`. Assigns
       * the next available index from the fetched snapshot so it doesn't reuse
       * the caller's default index. For edit-known-existing rules use
       * `patchTriggerWordRule` directly with the original index.
       */
      upsertBlacklistRule: builder.mutation<SecurityRule, UpsertBlacklistRuleRequest>({
        async queryFn({ chatId, rule, restore = false }, _api, _extra, baseQuery) {
          const placed = (index: number): TriggerRule => (restore ? rule : withDefaultBlacklistName(rule, index))

          const existingResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}`,
            method: "GET"
          })

          let updatedRule: SecurityRule

          if (existingResult.error) {
            if (!isNotFoundError(existingResult.error)) {
              return { error: toDuplicateWordsCustomError(existingResult.error) ?? existingResult.error }
            }

            // PUT `/api/v1/security/rules` is kind-scoped on the backend
            // (UpsertSecurityRulesUseCase keys by (chat_id, kind)), so this
            // creates a fresh trigger_words umbrella without touching sibling
            // security rules of other kinds (spam/captcha/length/ai_antispam).
            const firstRule = placed(0)
            const createResult = await baseQuery({
              url: `/api/v1/security/rules`,
              method: "PUT",
              body: {
                chat_id: chatId,
                is_enabled: true,
                kind: "trigger_words",
                warning: {
                  threshold: 0,
                  observation_interval: "P1D"
                },
                punishment: {
                  kind: "mute",
                  duration: "PT1H",
                  clean_count: 1,
                  skip_notification: false
                },
                config: {
                  rules: [firstRule]
                }
              } satisfies UpsertSecurityRuleRequest
            })

            if (createResult.error) {
              return { error: toDuplicateWordsCustomError(createResult.error) ?? createResult.error }
            }

            updatedRule = (createResult.data as UpdateSecurityRuleResponse).result.rules
            return { data: updatedRule }
          }

          const existingRule = getTriggerWordsRuleFromResponse(existingResult.data as GetSecurityRulesResponse)
          if (!existingRule) {
            // Same kind-scoped PUT contract as the 404 branch above: even when
            // the chat already has other security rules (the GET returned a
            // non-empty list), creating the trigger_words umbrella leaves them
            // intact because the backend upsert is keyed by (chat_id, kind).
            const firstRule = placed(0)
            const createResult = await baseQuery({
              url: `/api/v1/security/rules`,
              method: "PUT",
              body: {
                chat_id: chatId,
                is_enabled: true,
                kind: "trigger_words",
                warning: {
                  threshold: 0,
                  observation_interval: "P1D"
                },
                punishment: {
                  kind: "mute",
                  duration: "PT1H",
                  clean_count: 1,
                  skip_notification: false
                },
                config: {
                  rules: [firstRule]
                }
              } satisfies UpsertSecurityRuleRequest
            })

            if (createResult.error) {
              return { error: toDuplicateWordsCustomError(createResult.error) ?? createResult.error }
            }

            updatedRule = (createResult.data as UpdateSecurityRuleResponse).result.rules
            return { data: updatedRule }
          }

          // Pick the next index from the fetched snapshot because backend PATCH upserts by index.
          // Indexes below zero belong to rules migrated from custom commands.
          const nextIndex = Math.max(-1, ...existingRule.config.rules.map(existing => existing.index)) + 1
          const newRule = placed(nextIndex)

          const patchResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}/${existingRule.id}/trigger-rules`,
            method: "PATCH",
            body: { upsert: [newRule] }
          })

          if (patchResult.error) {
            return { error: toDuplicateWordsCustomError(patchResult.error) ?? patchResult.error }
          }

          const patchResponse = patchResult.data as PatchTriggerWordRulesResponse
          if (!patchResponse.result.rules) {
            return { error: { status: 500, data: "Trigger rules patch returned no rule" } }
          }
          updatedRule = patchResponse.result.rules
          return { data: updatedRule }
        },
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-trigger_words` },
                { type: "SafeList" as const, id: chatId },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      }),

      patchTriggerWordRule: builder.mutation<PatchTriggerWordRuleResponse, PatchTriggerWordRuleRequest>({
        async queryFn({ chatId, rule }, _api, _extra, baseQuery) {
          const existingResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}`,
            method: "GET"
          })

          if (existingResult.error) {
            return { error: existingResult.error }
          }

          const existingRule = getTriggerWordsRuleFromResponse(existingResult.data as GetSecurityRulesResponse)
          if (!existingRule) {
            return { error: { status: 404, data: "Trigger words rule not found" } }
          }
          const patchResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}/${existingRule.id}/trigger-rules`,
            method: "PATCH",
            body: {
              upsert: [rule]
            }
          })

          if (patchResult.error) {
            return { error: toDuplicateWordsCustomError(patchResult.error) ?? patchResult.error }
          }

          const response = patchResult.data as PatchTriggerWordRulesResponse
          if (!response.result.rules) {
            return { error: { status: 500, data: "Trigger rules patch returned no rule" } }
          }
          return { data: response.result.rules }
        },
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-trigger_words` },
                { type: "SafeList" as const, id: chatId },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      }),

      deleteProhibitedWordSecurityRule: builder.mutation<
        DeleteProhibitedWordSecurityRuleResponse,
        DeleteProhibitedWordSecurityRuleRequest
      >({
        async queryFn({ chatId, filterIndex }, _api, _extra, baseQuery) {
          const existingResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}`,
            method: "GET"
          })

          if (existingResult.error) {
            return { error: existingResult.error }
          }

          const existingRule = getTriggerWordsRuleFromResponse(existingResult.data as GetSecurityRulesResponse)
          if (!existingRule) {
            return { error: { status: 404, data: "Trigger words rule not found" } }
          }
          const patchResult = await baseQuery({
            url: `/api/v1/security/rules/chat/${chatId}/${existingRule.id}/trigger-rules`,
            method: "PATCH",
            body: {
              remove_indices: [filterIndex]
            }
          })

          if (patchResult.error) {
            return { error: patchResult.error }
          }

          const response = patchResult.data as PatchTriggerWordRulesResponse
          return { data: response.result.rules }
        },
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-trigger_words` },
                { type: "SafeList" as const, id: chatId },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      }),

      enableSecurityRule: builder.mutation<EnableSecurityRuleTransformedResponse, EnableSecurityRuleRequest>({
        query: ({ chatId, id }) => ({
          url: `/api/v1/security/rules/chat/${chatId}/${id}`,
          method: "PATCH",
          body: { is_enabled: true }
        }),
        transformResponse: (response: EnableSecurityRuleResponse) => {
          return response.result.rules
        },
        invalidatesTags: (_result, error, { chatId, id }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-${id}` },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      }),

      disableSecurityRule: builder.mutation<DisableSecurityRuleTransformedResponse, DisableSecurityRuleRequest>({
        query: ({ chatId, id }) => ({
          url: `/api/v1/security/rules/chat/${chatId}/${id}`,
          method: "PATCH",
          body: { is_enabled: false }
        }),
        transformResponse: (response: DisableSecurityRuleResponse) => {
          return response.result.rules
        },
        invalidatesTags: (_result, error, { chatId, id }) =>
          error
            ? []
            : [
                { type: "SecurityRules" as const, id: chatId },
                { type: "SecurityRule" as const, id: `${chatId}-${id}` },
                { type: "FeaturesStatus" as const, id: chatId }
              ]
      })
    })
  })

export const {
  useUpdateSecurityRuleMutation,
  useUpdateProhibitedWordSecurityRuleMutation,
  useUpsertBlacklistRuleMutation,
  usePatchTriggerWordRuleMutation,
  useDeleteProhibitedWordSecurityRuleMutation,
  useEnableSecurityRuleMutation,
  useDisableSecurityRuleMutation
} = updateSecurityRuleApi
